import { CampaignDetailsFields } from 'Components/campaigns/CampaignDetails';
import RadioGroup from 'Components/input/RadioGroup/RadioGroup';
import { PRIVILEGE } from 'constants/privilege';
import withAccessControl from 'hoc/withAccessControl';
import { usePrivileges } from 'hooks/useAuth';
import CampaignCreationLayout from 'Layouts/CampaignCreationLayout'
import moment from 'moment';
import React, { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Spinner } from 'reactstrap';
import { updateCampaignDetails, updateCampaignMilestone } from 'services/api/campaign';
import { RootState, AppDispatch } from 'store';
import { Milestone } from 'types/campaign/collaborations';

const toDatetimeLocal = (value?: string | null): string => {
    if (!value) return '';
    const parsed = moment(value);
    return parsed.isValid() ? parsed.format('YYYY-MM-DDTHH:mm') : '';
};

const toIso8601 = (value: string): string | null => {
    if (!value) return null;
    return moment(value, 'YYYY-MM-DDTHH:mm', true).toISOString();
};

const isCompleteDatetimeLocal = (value: string): boolean => {
    if (!value) return true;
    return moment(value, ['YYYY-MM-DDTHH:mm', 'YYYY-MM-DDTHH:mm:ss'], true).isValid();
};

const isFutureDatetimeLocal = (value: string): boolean => {
    if (!value) return true;
    return moment(value, ['YYYY-MM-DDTHH:mm', 'YYYY-MM-DDTHH:mm:ss'], true).isAfter(moment());
};

const CampaignPaymentTerms = () => {
    const { campaignId } = useParams();

    const milestonesData = useSelector((state: RootState) => state.campaignMilestones.items[campaignId!]?.milestonesData);
    const campaignMilestones = milestonesData?.milestones ?? [];
    const [errors, setErrors] = useState<{ [key: string]: string }>({});

    const dispatch: AppDispatch = useDispatch();
    const navigate = useNavigate();

    const [searchParams] = useSearchParams();
    const validate = searchParams.get('validate');
    const [validatedOnce, setValidatedOnce] = useState(false);
    const campaignDetails = useSelector((state: RootState) => state.campaignDetails.campaigns[(campaignId ?? 'create')]);
    const [fixedOffer, setFixedOffer] = useState<string>(campaignDetails?.campaign?.fixed_offer ?? 'y');
    const [applicationDeadline, setApplicationDeadline] = useState<string>(
        toDatetimeLocal(campaignDetails?.campaign?.application_deadline)
    );

    const [loading, setLoading] = React.useState<boolean>(false);
    const privileges = usePrivileges();
    const campaignMilestone = (Array.isArray(campaignMilestones) && campaignMilestones.length > 0) ? campaignMilestones[0] : null;

    const [milestone, setMilestone] = useState(campaignMilestone ?? null);

    useEffect(() => {
        setFixedOffer(campaignDetails?.campaign?.fixed_offer ?? 'y');
    }, [campaignDetails?.campaign?.fixed_offer])

    useEffect(() => {
        setApplicationDeadline(toDatetimeLocal(campaignDetails?.campaign?.application_deadline));
    }, [campaignDetails?.campaign?.application_deadline])

    useEffect(() => {
        setMilestone(campaignMilestone ?? null);
    }, [campaignMilestone])

    useEffect(() => {
        if (validate === 'true' && milestone && !validatedOnce) {
            validateMilestoneData();
            setValidatedOnce(true);
        }
    }, [validate, milestone, validatedOnce])

    const validateMilestoneData = () => {
        if (!milestone) return false;
        const validation_errors: { [key: string]: string } = {};
        if (milestone.gig_amount === '' || milestone.gig_amount == null || isNaN(Number(milestone.gig_amount)) || Number(milestone.gig_amount) < 0) {
            validation_errors.gig_amount = 'Please enter a valid amount.'
        }

        if (!milestone.payment_days || isNaN(Number(milestone.payment_days)) || Number(milestone.payment_days) < 0) {
            validation_errors.payment_days = 'Please enter a valid number of days.'
        }

        if (!isCompleteDatetimeLocal(applicationDeadline)) {
            validation_errors.application_deadline = 'Please enter a complete date and time, or clear the field.'
        }
        else if (!isFutureDatetimeLocal(applicationDeadline)) {
            validation_errors.application_deadline = 'Application deadline must be a future date and time.'
        }

        setErrors(validation_errors);

        return !(Object.values(validation_errors).length > 0);

    }

    const submitPaymentTerms = async () => {
        if (!campaignId || !milestone?.id || !validateMilestoneData()) return;

        setLoading(true);
        try {
            await updateCampaignDetails(campaignId, {
                fixed_offer: fixedOffer,
                application_deadline: toIso8601(applicationDeadline),
            } as unknown as CampaignDetailsFields);
            await updateCampaignMilestone(campaignId, String(milestone.id), milestone);
            navigate('/campaign/' + campaignId + '/banner');
        } catch (error) {
            toast.error('Failed to update milestone');
        } finally {
            setLoading(false);
        }
    }

    const handleMilestoneChange = (key: string, value: any) => {
        if (!milestone) return;
        const updatedMilestone: Milestone = {
            ...milestone,
            [key]: value
        };
        setMilestone(updatedMilestone);
    }

    const handleSpecificationsChange = (key: string, value: any) => {
        if (!milestone) return;
        const updatedMilestone: Milestone = {
            ...milestone,
            specifications: {
                ...milestone.specifications,
                [key]: value
            }
        };
        setMilestone(updatedMilestone);
    }


    return (
        <CampaignCreationLayout
            activeStep='payment-terms'
            actionRow={
                <div className="d-flex justify-content-between mb-4">
                    <div></div>
                    <button type='button' onClick={submitPaymentTerms} disabled={loading || !privileges.includes(PRIVILEGE.CAMPAIGN.CAMPAIGN.EDIT)} className="theme-btn theme-btn-lg btn-gradient form-continue-btn d-flex justify-content-between align-items-center">
                        <React.Fragment>
                            {
                                'Proceed to Next'
                            }
                            {
                                loading ? <Spinner size="sm" className='me-2'> Loading... </Spinner> : <i className='bx fs-4 bx-right-arrow-alt' ></i>
                            }
                        </React.Fragment>
                    </button>
                </div>
            }
        >
            <div>
                <div className="form-group m-0">
                    <div className="d-flex flex-column">
                        <label htmlFor="max-gigs">Fee per creator <span className="text-danger">*</span></label>
                        <span className='field-desc'>Enter the fee to be paid to each creator for this campaign.</span>
                    </div>
                    <input
                        type="number"
                        id="influencer-budget"
                        className="form-control"
                        value={milestone?.gig_amount ?? ''}
                        onChange={(e) => handleMilestoneChange('gig_amount', e.target.value === '' ? '' : Number(e.target.value))}
                        placeholder="Enter fee per creator" />
                    {errors['gig_amount'] &&
                        <span className="text-danger">{errors['gig_amount']}</span>
                    }
                </div>
                <div className="form-group mt-4">
                    <div className="d-flex flex-column">
                        <label htmlFor="max-gigs">Fixed Offer? <span className="text-danger">*</span></label>
                        <span className='field-desc'>Select if the offered fee is fixed.</span>
                    </div>
                    <RadioGroup
                        options={[{ label: 'Yes', id: 'y' }, { label: 'No', id: 'n' }]}
                        name="fixed_offer"
                        defaultValue={fixedOffer}
                        onChange={(val) => setFixedOffer(val)}
                    />
                </div>
                <div className="form-group mt-4">
                    <div className="d-flex flex-column">
                        <label htmlFor="max-gigs">Payment Days <span className="text-danger">*</span></label>
                        <span className='field-desc'>Enter the number of days after collaboration is completed when creators will receive their payment.</span>
                    </div>
                    <input
                        type="number"
                        id="payment-days"
                        className="form-control"
                        value={milestone?.payment_days ?? ''}
                        onChange={(e) => handleMilestoneChange('payment_days', e.target.value === '' ? '' : Number(e.target.value))}
                        placeholder="Enter fee per creator" />
                    {errors['payment_days'] &&
                        <span className="text-danger">{errors['payment_days']}</span>
                    }
                </div>
                <div className="form-group mt-4">
                    <div className="d-flex flex-column">
                        <label htmlFor="application-deadline">Application Deadline</label>
                        <span className='field-desc'>Last Date and Time until when creators can apply to this campaign.</span>
                    </div>
                    <div className="position-relative">
                        <input
                            type="datetime-local"
                            id="application-deadline"
                            className="form-control"
                            style={{ paddingRight: applicationDeadline ? '2.25rem' : undefined }}
                            value={applicationDeadline}
                            onChange={(e) => {
                                setApplicationDeadline(e.target.value);
                                if (errors.application_deadline) {
                                    setErrors((prev) => {
                                        const { application_deadline: _, ...rest } = prev;
                                        return rest;
                                    });
                                }
                            }}
                        />
                        {applicationDeadline &&
                            <button
                                type="button"
                                className="unset-all cursor-pointer position-absolute d-flex align-items-center justify-content-center"
                                style={{ right: '0.65rem', top: '50%', transform: 'translateY(-50%)', lineHeight: 1 }}
                                onClick={() => {
                                    setApplicationDeadline('');
                                    setErrors((prev) => {
                                        const { application_deadline: _, ...rest } = prev;
                                        return rest;
                                    });
                                }}
                                aria-label="Clear application deadline"
                            >
                                <i className="bx bx-x fs-5 text-muted" />
                            </button>
                        }
                    </div>
                    {errors['application_deadline'] &&
                        <span className="text-danger">{errors['application_deadline']}</span>
                    }
                </div>
            </div>
        </CampaignCreationLayout>
    )
}

export default withAccessControl(CampaignPaymentTerms, PRIVILEGE.CAMPAIGN.CAMPAIGN.EDIT)
